// Overview (prototype `pages/Dashboard.tsx`). Each enabled module contributes a section to
// `/dashboard`; the core sections (accounting, finance, sales, purchasing, integrations,
// activity) make up the prototype's layout, optional modules add tiles through `dashboardTiles`.
import {
  compact,
  date,
  daysBetween,
  label,
  money,
  money0,
  monthLabel,
  pct,
  relTime,
  slotEntries,
  todayIso,
  useApi,
  useAuth,
} from "@keel/core";
import { Icon, Skeleton, Stat, type Task, TaskCheck, useCrumbs, useRecordHref } from "@keel/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

interface Open {
  amount: number;
  count: number;
  overdue: number;
  overdueCount: number;
}
interface Month {
  month: string;
  revenue: number;
  cogs: number;
  gross: number;
}
interface Health {
  id: string;
  name: string;
  status: string;
  message: string;
}
interface Sections {
  accounting?: {
    revenueMtd: number;
    revenuePrevMtd: number;
    bank: number;
    inventory: number;
    series: Month[];
  };
  finance?: {
    receivable: Open;
    payable: Open;
    overdue: { id: string; number: string; dueDate: string; balance: number; party: string | null }[];
    draftInvoices: number;
    billsToPost: number;
  };
  sales?: {
    toConfirm: number;
    toDeliver: number;
    toShip: number;
    toInvoice: number;
    channels: { channel: string; orders: number; revenue: number }[];
  };
  purchasing?: { toReceive: number; lateDeliveries: number };
  integrations?: { health: Health[] };
  platform?: {
    /** Open tasks given to me (ERP-73); everyone has these. */
    tasks?: { open: number; overdue: number; items: Task[] } | null;
    /** Background work that failed and is not dealt with (ERP-123); for who may read the audit log. */
    jobFailures?: number;
    /** Only for who may read the audit log. */
    activity?: {
      at: string;
      action: string;
      entityType: string;
      entityId: string;
      entityLabel: string | null;
      actorName: string | null;
    }[];
  };
  [module: string]: unknown;
}

const ENTITY_HREF: Record<string, string> = {
  SalesOrder: "/orders/",
  PurchaseOrder: "/purchase-orders/",
  Invoice: "/invoices/",
  Bill: "/bills/",
  DeliveryNote: "/deliveries/",
  Customer: "/customers/",
  Supplier: "/suppliers/",
  Product: "/products/",
  IntegrationConnection: "/integrations/",
  ProductionOrder: "/production-orders/",
  FixedAsset: "/assets/",
  LoanAgreement: "/loans/",
  PayRun: "/payroll/runs/",
};
const ENTITY_LABEL: Record<string, string> = {
  SalesOrder: "order",
  PurchaseOrder: "purchase order",
  DeliveryNote: "delivery",
  IntegrationConnection: "integration",
  ProductVariant: "variant",
  WebhookSubscription: "webhook",
  ApiKey: "API key",
  ProductionOrder: "production order",
  FixedAsset: "asset",
  LoanAgreement: "loan",
  PayRun: "pay run",
};

export function Dashboard() {
  const { me, company, can } = useAuth();
  const nav = useNavigate();
  const { data } = useApi<{ today: string; sections: Sections }>("/dashboard", { refetchInterval: 30_000 });
  useCrumbs([{ label: "Overview" }]);
  if (!data) return <Skeleton rows={10} />;
  const s = data.sections;
  const a = s.accounting;
  const f = s.finance;
  const delta = a?.revenuePrevMtd ? (a.revenueMtd - a.revenuePrevMtd) / a.revenuePrevMtd : null;
  const hour = new Date().getHours();
  const last12 = a?.series.reduce((x, m) => x + m.revenue, 0) ?? 0;
  const gross12 = a?.series.reduce((x, m) => x + m.gross, 0) ?? 0;
  const queue = [
    s.sales && { n: s.sales.toConfirm, label: "Orders to confirm", to: "/orders?tab=draft" },
    s.sales && { n: s.sales.toDeliver, label: "Orders to deliver", to: "/orders?tab=to_deliver" },
    s.sales && { n: s.sales.toShip, label: "Delivery notes to ship", to: "/deliveries?tab=draft" },
    s.sales && { n: s.sales.toInvoice, label: "Delivered, not invoiced", to: "/orders?tab=delivered" },
    s.purchasing && {
      n: s.purchasing.toReceive,
      label: "Purchases awaiting receipt",
      to: "/purchase-orders?tab=open",
    },
    f && { n: f.billsToPost, label: "Bills to post", to: "/bills?tab=draft" },
  ].filter((x): x is { n: number; label: string; to: string } => !!x);
  const health = s.integrations?.health ?? [];
  const bad = health.filter((h) => h.status !== "healthy");
  const tiles = slotEntries("dashboardTiles", me.modules, can).filter((t) => s[t.section] != null);
  const channels = s.sales?.channels ?? [];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>
            {hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening"},{" "}
            {me.user?.name.split(" ")[0] ?? me.actor}
          </h1>
          <p className="sub">
            {company.name} ·{" "}
            {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
      </div>

      {s.platform?.jobFailures ? (
        <div className="banner banner-warn" data-testid="job-failures">
          {s.platform.jobFailures === 1
            ? "A background job is failing"
            : `${s.platform.jobFailures} background jobs are failing`}{" "}
          — interest accruals, reminders or other work Keel does on its own.{" "}
          <Link className="link" to="/settings?tab=jobs">
            See what went wrong
          </Link>
        </div>
      ) : null}

      {/* Work given to me comes first while there is any (ERP-73). */}
      {s.platform?.tasks?.open ? <MyTasks tasks={s.platform.tasks} /> : null}

      {(a || f) && (
        <section className="stat-strip">
          {a && (
            <Stat
              label="Revenue this month"
              value={money0(a.revenueMtd)}
              href="/reports?period=mtd"
              foot={
                delta == null ? (
                  "No prior data"
                ) : (
                  <>
                    <span className={`delta ${delta >= 0 ? "up" : "down"}`}>
                      <Icon name={delta >= 0 ? "arrowUp" : "arrowDown"} size={12} />
                      {pct(Math.abs(delta))}
                    </span>{" "}
                    vs same point last month
                  </>
                )
              }
            />
          )}
          {f && (
            <Stat
              label="Receivables"
              value={money0(f.receivable.amount)}
              href="/invoices?tab=open"
              foot={
                f.receivable.overdueCount ? (
                  <>
                    <span className="tone-danger">{money0(f.receivable.overdue)} overdue</span> ·{" "}
                    {f.receivable.overdueCount} invoices
                  </>
                ) : (
                  `${f.receivable.count} open invoices`
                )
              }
            />
          )}
          {f && (
            <Stat
              label="Payables"
              value={money0(f.payable.amount)}
              href="/bills?tab=open"
              foot={`${f.payable.count} open bills`}
            />
          )}
          {a && (
            <Stat
              label="Cash in bank"
              value={money0(a.bank)}
              href="/ledger?account=1000"
              foot={f ? `After open bills ${money0(a.bank - f.payable.amount)}` : "Ledger account 1000"}
            />
          )}
          {a && (
            <Stat
              label="Inventory at cost"
              value={money0(a.inventory)}
              href="/inventory"
              foot="Goods and raw materials"
            />
          )}
        </section>
      )}

      {tiles.length > 0 && (
        <section className="stat-strip">
          {tiles.map((t) => (
            <t.component key={t.key} data={s[t.section]} />
          ))}
        </section>
      )}

      {health.length > 0 && (
        <section className="card">
          <header className="card-head">
            <div>
              <h2>Integration health</h2>
              <p className="sub">
                {health.length - bad.length} of {health.length} connections healthy
              </p>
            </div>
            <Link className="link" to="/integrations">
              Open hub
            </Link>
          </header>
          <div
            className="card-body"
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 8 }}
          >
            {health.map((h) => (
              <Link
                key={h.id}
                to={`/integrations/${h.id}`}
                className="row"
                style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--line)" }}
              >
                <span className={`dot ${h.status}`} />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <div
                    className="strong"
                    style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {h.name.split(" — ")[0]}
                  </div>
                  <div className={`sm tone-${h.status}`}>{h.message}</div>
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {(a || queue.length > 0) && (
        <div className="grid-dash">
          {a && (
            <section className="card chart-card">
              <header className="card-head">
                <div>
                  <h2>Revenue</h2>
                  <p className="sub">
                    <Link className="link" to="/reports?period=l12">
                      Last 12 months
                    </Link>{" "}
                    · {compact(last12)} · {pct(last12 ? gross12 / last12 : null)} gross margin
                  </p>
                </div>
                <span className="sub">Pick a month</span>
              </header>
              <BarChart series={a.series} onPick={(m) => nav(`/reports?month=${m.slice(0, 7)}`)} />
            </section>
          )}
          {queue.length > 0 && (
            <section className="card">
              <header className="card-head">
                <h2>Needs attention</h2>
              </header>
              <ul className="queue">
                {queue.map((it) => (
                  <li key={it.label}>
                    <Link to={it.to} className={it.n ? "" : "is-zero"}>
                      <span className="queue-n">{it.n}</span>
                      <span className="queue-label">{it.label}</span>
                      <Icon name="chevron" size={14} />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      <div className="grid-3">
        {s.platform?.tasks && !s.platform.tasks.open && <MyTasks tasks={s.platform.tasks} />}
        {f && (
          <section className="card">
            <header className="card-head">
              <h2>Overdue invoices</h2>
              <Link className="link" to="/invoices?tab=overdue">
                View all
              </Link>
            </header>
            {f.overdue.length ? (
              <MiniTable
                rows={f.overdue.map((o) => ({
                  to: `/invoices/${o.id}`,
                  left: (
                    <>
                      <div className="strong">{o.party}</div>
                      <div className="muted mono sm">{o.number}</div>
                    </>
                  ),
                  right: (
                    <>
                      <div className="num strong">{money(o.balance)}</div>
                      <div className="sm tone-danger">{daysBetween(o.dueDate, todayIso())} days late</div>
                    </>
                  ),
                }))}
              />
            ) : (
              <p className="muted pad">Nothing overdue.</p>
            )}
          </section>
        )}
        {s.sales && (
          <section className="card">
            <header className="card-head">
              <h2>Orders by channel</h2>
              <span className="sub">Last 30 days</span>
            </header>
            <MiniTable
              rows={channels.map((c) => ({
                to: `/orders?channel=${c.channel}`,
                left: (
                  <>
                    <div className="strong">{label(c.channel === "d365" ? "Dynamics" : c.channel)}</div>
                    <div className="bar-track">
                      <div
                        className="bar-fill"
                        style={{
                          width: `${(c.revenue / Math.max(...channels.map((x) => x.revenue), 1)) * 100}%`,
                        }}
                      />
                    </div>
                  </>
                ),
                right: (
                  <>
                    <div className="num strong">{compact(c.revenue)}</div>
                    <div className="sm muted">{c.orders} orders</div>
                  </>
                ),
              }))}
            />
          </section>
        )}
        {s.platform?.activity && (
          <section className="card">
            <header className="card-head">
              <h2>Recent activity</h2>
              <Link className="link" to="/audit">
                Audit log
              </Link>
            </header>
            <ol className="feed">
              {s.platform.activity.slice(0, 7).map((x) => {
                const href = ENTITY_HREF[x.entityType];
                // Audit actions are keys ("commission_billed"); the feed reads them as words.
                const text = `${x.actorName ?? "System"} ${x.action.replaceAll("_", " ")} ${ENTITY_LABEL[x.entityType] ?? x.entityType.toLowerCase()} ${x.entityLabel ?? ""}`;
                return (
                  <li key={`${x.at}-${x.entityId}-${x.action}`}>
                    <span className={`feed-dot feed-${x.action === "created" ? "doc" : "stock"}`} />
                    <span className="feed-text">
                      {href ? <Link to={href + x.entityId}>{text}</Link> : text}
                    </span>
                    <time className="muted">{relTime(x.at)}</time>
                  </li>
                );
              })}
            </ol>
          </section>
        )}
      </div>
    </div>
  );
}

/** What has been given to me to do, overdue and soonest first (ERP-73). */
function MyTasks({ tasks }: { tasks: { open: number; overdue: number; items: Task[] } }) {
  const href = useRecordHref();
  return (
    <section className="card">
      <header className="card-head">
        <h2>My tasks</h2>
        <span className="sub">
          {tasks.open ? `${tasks.open} open` : ""}
          {tasks.overdue ? <span className="tone-danger"> · {tasks.overdue} overdue</span> : null}
        </span>
        <Link className="link" to="/tasks">
          View all
        </Link>
      </header>
      {tasks.items.length ? (
        <ol className="feed">
          {tasks.items.map((t) => {
            const to = href(t.entityType, t.entityId);
            const late = t.dueOn && t.dueOn < todayIso();
            return (
              <li key={t.id} className="task-row">
                <TaskCheck task={t} />
                <span className="feed-text">
                  <span className="strong">
                    {t.priority === "high" && <span className="tone-danger">! </span>}
                    {to ? <Link to={to}>{t.title}</Link> : t.title}
                  </span>
                  <span className="sm muted">
                    {" "}
                    {[
                      t.category && `${t.category} ${t.entityLabel ?? ""}`.trim(),
                      t.createdByName && `from ${t.createdByName}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                {t.dueOn && <time className={late ? "tone-danger" : "muted"}>{date(t.dueOn)}</time>}
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="muted pad">Nothing assigned to you. Tasks people give you from notes show here.</p>
      )}
    </section>
  );
}

function MiniTable({ rows }: { rows: { to: string; left: ReactNode; right: ReactNode }[] }) {
  const nav = useNavigate();
  return (
    <table className="mini">
      <tbody>
        {rows.map((r) => (
          <tr key={r.to} data-href="1" onClick={() => nav(r.to)}>
            <td>{r.left}</td>
            <td className="r">{r.right}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function niceStep(raw: number) {
  const p = 10 ** Math.floor(Math.log10(raw || 1));
  const n = raw / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p;
}

/** Single-series monthly bars; the in-progress month is lighter. Drawn at real width so text stays 11px. */
function BarChart({ series, onPick }: { series: Month[]; onPick?: (month: string) => void }) {
  const host = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(640);
  const [hover, setHover] = useState<number | null>(null);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => e && setW(Math.max(320, e.contentRect.width - 24)));
    if (host.current) ro.observe(host.current);
    return () => ro.disconnect();
  }, []);
  const H = 250;
  const padL = 48;
  const padR = 8;
  const padT = 16;
  const padB = 28;
  const max = Math.max(...series.map((s) => s.revenue), 1);
  const step = niceStep(max / 4);
  const top = Math.ceil(max / step) * step;
  const iw = w - padL - padR;
  const ih = H - padT - padB;
  const bw = iw / series.length;
  const barW = Math.min(34, bw - 10);
  const y = (v: number) => padT + ih - (v / top) * ih;
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  const last = series.length - 1;
  const h = hover != null ? series[hover] : null;
  return (
    <div className="chart" ref={host}>
      <svg
        viewBox={`0 0 ${w} ${H}`}
        role="img"
        aria-label="Monthly revenue"
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line className="grid" x1={padL} x2={w - padR} y1={y(t)} y2={y(t)} />
            <text className="axis" x={padL - 8} y={y(t) + 4} textAnchor="end">
              {compact(t)}
            </text>
          </g>
        ))}
        {series.map((s, i) => {
          const x = padL + i * bw + (bw - barW) / 2;
          const hh = Math.max(0, padT + ih - y(s.revenue));
          const r = Math.min(4, hh / 2);
          const yt = y(s.revenue);
          return (
            // A bar is a month, and a month is a question: which accounts made that revenue.
            // Clicking opens the profit & loss for it. The group carries a role and a tab stop
            // when it is pickable, which the rule cannot see through the conditional.
            // biome-ignore lint/a11y/noStaticElementInteractions: role and tabIndex are set above
            <g
              key={s.month}
              className={`bar ${i === last ? "partial" : ""} ${hover === i ? "hover" : ""} ${onPick ? "pickable" : ""}`}
              role={onPick ? "button" : undefined}
              tabIndex={onPick ? 0 : undefined}
              aria-label={onPick ? `Revenue for ${s.month}` : undefined}
              onMouseEnter={() => setHover(i)}
              onClick={() => onPick?.(s.month)}
              onKeyDown={(e) => {
                if (onPick && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onPick(s.month);
                }
              }}
            >
              <rect className="hit" x={padL + i * bw} y={padT} width={bw} height={ih} />
              {hh > 0 && (
                <path
                  d={`M${x},${padT + ih} V${yt + r} Q${x},${yt} ${x + r},${yt} H${x + barW - r} Q${x + barW},${yt} ${x + barW},${yt + r} V${padT + ih} Z`}
                />
              )}
              <text className="axis" x={x + barW / 2} y={H - 8} textAnchor="middle">
                {monthLabel(s.month)}
                {i === last && bw > 44 ? " · MTD" : ""}
              </text>
            </g>
          );
        })}
      </svg>
      {h && hover != null && (
        <div
          className="tip"
          style={{
            left: Math.min(Math.max(padL + hover * bw + bw / 2 + 8, 90), w - 80),
            top: Math.max(0, y(h.revenue) - 12),
          }}
        >
          <div className="tip-title">
            {new Date(`${h.month}-01T00:00:00Z`).toLocaleDateString("en-GB", {
              month: "long",
              year: "numeric",
              timeZone: "UTC",
            })}
          </div>
          <div className="tip-row">
            <span>Revenue</span>
            <b>{money0(h.revenue)}</b>
          </div>
          <div className="tip-row">
            <span>Cost of goods</span>
            <b>{money0(h.cogs)}</b>
          </div>
          <div className="tip-row">
            <span>Gross margin</span>
            <b>{h.revenue ? pct(h.gross / h.revenue) : "—"}</b>
          </div>
        </div>
      )}
    </div>
  );
}
